import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtTime } from "../api";
import { CheckCircle2, Clock, Megaphone, Loader2 } from "lucide-react";

/**
 * Sender-side aggregate view of a broadcast notice with mandatory "Entendi":
 * who already confirmed (with time) and who is still pending.
 */
export function AckStatusDialog({ open, broadcastId, onClose }) {
  const { user, t } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !broadcastId) return;
    setData(null);
    setError(false);
    api.get(`/broadcast/${broadcastId}/acks?user_id=${user.id}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [open, broadcastId, user.id]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm z-[260]" data-testid="ack-status-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            {t("ack_dialog_title")}
            {data && (
              <span className="text-sm font-normal text-muted-foreground" data-testid="ack-status-count">
                {data.confirmed}/{data.total}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {!data && !error && (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
        {error && <p className="text-sm text-destructive text-center py-6">{t("something_wrong")}</p>}
        {data && (
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {data.confirmed === data.total && data.total > 0 && (
              <div className="text-xs font-medium text-[hsl(var(--success))] flex items-center gap-1.5 pb-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> {t("ack_all_confirmed")}
              </div>
            )}
            {data.recipients.map((r) => (
              <div
                key={r.recipient_id || r.message_id}
                className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5"
                data-testid={r.acked_at ? "ack-row-confirmed" : "ack-row-pending"}
              >
                {r.acked_at ? (
                  <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] shrink-0" />
                ) : (
                  <Clock className="h-5 w-5 text-muted-foreground/60 shrink-0" />
                )}
                <span className="text-sm font-medium flex-1 min-w-0 truncate">{r.recipient_name}</span>
                <span className={`text-xs shrink-0 ${r.acked_at ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                  {r.acked_at ? `${t("ack_confirmed")} · ${fmtTime(r.acked_at)}` : t("ack_pending")}
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
